import { Grid, TextField, MenuItem, InputLabel, Select } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import pick from "lodash/pick";
import { useTranslation } from "react-i18next";
import { useShopUpdate } from "queries/shops";
import { Api } from "queries/types";
import GridOnIcon from "@mui/icons-material/GridOn";
import QueueIcon from "@mui/icons-material/Queue";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import HistoryIcon from '@mui/icons-material/History';
import Paper from "./Paper";
import Field from "./Field";
import { Dispatch, SetStateAction, useState } from "react";
import { Feature, useAuthorization } from "user";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { download } from "queries/http";
import InventoryProposalModal from "../index/InventoryProposalModal";
import GeneralInformationRedirection from "./GeneralInformationRedirection";

interface Props {
  shop: Api.Shop;
  setShop: (shop: Api.Shop) => void;
  onSubmitted: () => void;
}
interface CardFooterButtonsForShopProps {
  shop: Api.Shop;
  hasAccess: (feature: Feature) => boolean;
  t: (key: string) => string;
  navigate: NavigateFunction;
  setInventoryProposalShop: Dispatch<SetStateAction<Api.Shop | undefined>>;
}

interface OptionsButton {
    Icon: React.ElementType;
    text: string;
    onClick: () => void;
}

const getGeneralInformationOptions = ({ shop, hasAccess, t, navigate, setInventoryProposalShop }: CardFooterButtonsForShopProps): OptionsButton[] => {
  const inventoryAvailable = shop.shopGrids.filter((grid) => grid.isActive).length > 0;
  const hasRecordedCellsCount = inventoryAvailable && shop.recordableCellsCount >= 1;
  const recordedCellsAvailable = hasRecordedCellsCount && shop.recordedCellsCount >= 1;
  const hasHistory = inventoryAvailable && shop.shopGrids && shop.shopGrids.length > 1;
  const generalInformationOptions = [];

  if (hasAccess(Feature.ShopPropose) && recordedCellsAvailable) {
    generalInformationOptions.push({
      Icon: QueueIcon,
      text: t("shop.index.propose"),
      onClick: () => setInventoryProposalShop(shop)
    });
  }

  if (hasRecordedCellsCount) {
    generalInformationOptions.push({
      Icon: GridOnIcon,
      text: t("shop.index.inventory"),
      onClick: () => navigate(`/shops/${shop.erpId}/inventory`),
    });
  }

  if (hasAccess(Feature.ShopComparator) && hasHistory) {
    generalInformationOptions.push({
      Icon: HistoryIcon,
      text: t("shop.index.compare"),
      onClick: () => navigate(`/shops/${shop.erpId}/compare`)
    });
  }

  if (recordedCellsAvailable) {
    generalInformationOptions.push({
      Icon: FileDownloadIcon,
      text: t("shop.index.pdf"),
      onClick: () => download(`/api/shops/${shop.erpId}/map/pdf/`),
    });
  }

  return generalInformationOptions;
};


const GeneralInformationStep = ({ shop, setShop, onSubmitted }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasAccess = useAuthorization();
  const mutation = useShopUpdate(shop.erpId);
  const [inventoryProposalShop, setInventoryProposalShop] = useState<Api.Shop>();

  const generalInformationOptions = getGeneralInformationOptions({ shop, hasAccess, t, navigate, setInventoryProposalShop });

  return (
    <Paper>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          const values = pick(shop, "isActive");
          mutation.mutateAsync(values).then(onSubmitted);
        }}
      >
        <Grid container spacing={4}>
          <Field>
            <TextField label={t("shop.erpId")} variant="standard" value={shop.erpId} disabled />
          </Field>
          <Field>
            <TextField label={t("shop.brand")} variant="standard" value={shop.brandName} disabled />
          </Field>
          <Field>
            <TextField
              label={t("shop.salesManager")}
              variant="standard"
              fullWidth
              disabled
              value={shop.shopManagerFullName}
            />
          </Field>
          <Field>
            <TextField label={t("shop.country")} variant="standard" value={shop.country} disabled />
          </Field>
          <Field>
            <TextField label={t("shop.city")} variant="standard" value={shop.town} disabled />
          </Field>
          <Field>
            <TextField label={t("shop.sectorManager")} variant="standard" disabled value={shop.sectorManagerFullName} />
          </Field>
          <Field>
            <InputLabel>{t("shop.status")}</InputLabel>
            <Select
              required
              label={t("shop.status")}
              value={shop.isActive ? "active" : "inactive"}
              onChange={(e) => setShop({ ...shop, isActive: e.target.value === "active" })}
              name="isActive"
            >
              <MenuItem value="active">{t("shop.statuses.active")}</MenuItem>
              <MenuItem value="inactive">{t("shop.statuses.inactive")}</MenuItem>
            </Select>
          </Field>
        </Grid>
        <Grid container justifyContent={generalInformationOptions.length ? 'space-between' : 'flex-end'} sx={{ mt: { sm: 1, xs: 2 } }}>
          <GeneralInformationRedirection options={generalInformationOptions} />
          <LoadingButton type="submit" variant="contained" loading={mutation.isLoading} sx={{ justifySelf: 'end' }}>
            {t("shop.step2")}
          </LoadingButton>
        </Grid>
      </form>
      {inventoryProposalShop && (
        <InventoryProposalModal shop={inventoryProposalShop} onClose={(gridId?: number) => {
          inventoryProposalShop.shopGrids = inventoryProposalShop.shopGrids.filter(shopGrid => shopGrid.id !== gridId);
          setInventoryProposalShop(undefined)
        }} />
      )}
    </Paper>
  );
};

export default GeneralInformationStep;
